/**
 * Cart Service Types
 *
 * Defines the interfaces for the database-backed cart system.
 * Uses a minimal 2-table design: workflow_carts + asset_reservations.
 * The API returns full asset entities with item_source so frontend can filter by tab.
 */

import type { Entity } from '@/types/Entity';

// =============================================================================
// ENTITY TYPES (from database)
// =============================================================================

export enum ScanningStatus {
  NotStarted = 'not_started',
  ReadyToScan = 'ready_to_scan',
  InProgress = 'in_progress',
  Complete = 'complete',
}

/**
 * Workflow type for cart operations.
 */
export type WorkflowType =
  | 'issue'
  | 'exchange'
  | 'turn_in'
  | 'maintenance-send'
  | 'maintenance-receive'
  | 'transfer'
  | 'shipment';

/**
 * Workflow cart entity from database.
 */
export interface WorkflowCart {
  id: string;
  customer_id: string;
  workflow_type: WorkflowType;
  created_at: string;
  updated_at: string;
  customer_display_name?: string | null;
  /** Total items in cart (reserved + unsized) */
  item_count?: number;
  /** Actual asset reservations (sized items with locked stock) */
  reserved_count?: number;
  /** Placeholders awaiting size selection (no stock locked) */
  unsized_count?: number;
  scanning_status?: ScanningStatus;
  /** Display name of the user who has claimed this cart for scanning */
  claimed_by_display_name?: string | null;
  /** User-entered document number saved with the cart (maintenance workflows) */
  document_number?: string | null;
  /** Linked maintenance request ID (maintenance-receive workflows) */
  maintenance_request_id?: string | null;
}

/**
 * Reserved asset with its source tab.
 */
export interface ReservedAsset {
  entity: Entity;
  item_source: 'ie' | 'additional';
  ie_kit_id?: string | null;
  size?: string | null;
}

/**
 * Size allocation cart placeholder representing an unsized catalog item
 * awaiting size selection and asset allocation. Placeholders do not reserve
 * stock; they are fulfilled later by creating concrete asset_reservation rows
 * (e.g., via the mobile scanning workflow).
 */
export interface SizeAllocationCart {
  id: string;
  cart_id: string;
  parent_catalog_id: string;
  quantity: number;
}

// =============================================================================
// API PARAMETERS
// =============================================================================

export interface AddToCartParams {
  customerId: string;
  assetIds: string[];
  itemSource?: 'ie' | 'additional';
  ieKitId?: string | null;
  workflowType?: WorkflowType;
}

export interface RemoveFromCartParams {
  customerId: string;
  assetIds: string[];
  workflowType?: WorkflowType;
}

export interface BulkAllocateItem {
  quantity: number;
  catalog_id: string;
  item_name: string;
  ie_kit_id?: string | null;
  location_id?: string | null;
}

export interface BulkAllocateParams {
  customerId: string;
  items: BulkAllocateItem[];
  excludeAssetIds?: string[];
  /** When true, suppress "Limited inventory" toast (used during auto-populate) */
  silent?: boolean;
}

export interface BulkAllocateItemResult {
  catalog_id: string;
  item_name: string;
  requested: number;
  allocated: number;
  asset_ids: string[];
}

export interface BulkAllocateResult {
  success: boolean;
  cart_id?: string;
  allocated_count?: number;
  items?: BulkAllocateItemResult[];
  error?: string;
}

// =============================================================================
// API RESPONSES
// =============================================================================

export interface AddToCartResult {
  success: boolean;
  cart_id?: string;
  added_count?: number;
  already_reserved?: string[];
  error?: string;
}

export interface RemoveFromCartResult {
  success: boolean;
  removed_count?: number;
  error?: string;
}

export interface ClearCartResult {
  success: boolean;
  cart_id?: string;
  reservations_released?: number;
  message?: string;
}

/**
 * Cart result with reserved assets including item_source.
 */
export interface GetCartResult {
  cart: WorkflowCart | null;
  assets: ReservedAsset[];
  placeholders: SizeAllocationCart[];
}

// =============================================================================
// SERVICE INTERFACE
// =============================================================================

export interface ICartService {
  getCart(customerId: string, workflowType?: WorkflowType): Promise<GetCartResult>;
  getAllActiveCarts(): Promise<WorkflowCart[]>;
  addToCart(params: AddToCartParams): Promise<AddToCartResult>;
  removeFromCart(params: RemoveFromCartParams): Promise<RemoveFromCartResult>;
  removeReservationsByAssetIds(
    customerId: string,
    workflowType: WorkflowType,
    assetIds: string[],
  ): Promise<{ success: boolean; reservations_released: number }>;
  clearCart(customerId: string, workflowType?: WorkflowType, options?: { allowActiveScan?: boolean }): Promise<ClearCartResult>;
  deleteCart(cartId: string): Promise<void>;
  updateScanningStatus(cartId: string, status: ScanningStatus): Promise<void>;
  updateDocumentNumber(cartId: string, documentNumber: string): Promise<void>;
  updateMaintenanceRequestId(cartId: string, maintenanceRequestId: string): Promise<void>;
  cancelSync(cartId: string): Promise<void>;
  bulkAllocateToCart(params: BulkAllocateParams): Promise<BulkAllocateResult>;
  upsertSizeAllocationCart(params: {
    customerId: string;
    parentCatalogId: string;
    quantity: number;
  }): Promise<{
    success: boolean;
    cart_id: string | null;
    placeholder_id: string | null;
    quantity: number;
    error?: string;
  }>;
  bulkUpsertSizeAllocationCart(params: {
    customerId: string;
    items: Array<{ parentCatalogId: string; quantity: number }>;
  }): Promise<{
    success: boolean;
    cart_id: string | null;
    created_count: number;
    updated_count: number;
    error?: string;
  }>;
  clearSizeAllocationCarts(customerId: string): Promise<{
    success: boolean;
    cart_id: string | null;
    cleared: number;
    error?: string;
  }>;
}
